"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import styles from "../css/components/multiSelectDropdown.module.css";

/**
 * MultiSelectDropdown
 * - Checkbox-based dropdown that allows selecting many options
 * - Scrolls when there are more than 3 options visible
 */
export default function MultiSelectDropdown({
  label,
  name,
  options,
  value,
  onChange,
  placeholder = "Select options",
  maxVisible = 3,
  disabled = false,
  className = "",
  dropdownClassName = "",
  compact = false,
}) {
  const containerRef = useRef(null);
  const [open, setOpen] = useState(false);

  // Normalize options to objects { value, label }
  const normalizedOptions = useMemo(() => {
    return (options || []).map((opt) =>
      typeof opt === "string"
        ? { value: opt, label: opt }
        : { value: opt.value, label: opt.label ?? String(opt.value) }
    );
  }, [options]);

  const selected = Array.isArray(value) ? value : [];

  const toggleOpen = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
  };

  const close = () => setOpen(false);

  // Close when clicking outside
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) close();
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const isChecked = (val) => selected.includes(val);

  const handleToggle = (val) => {
    if (!onChange) return;
    const exists = isChecked(val);
    const next = exists ? selected.filter((v) => v !== val) : [...selected, val];
    onChange(next);
  };

  const selectedLabel = useMemo(() => {
    if (!selected.length) return placeholder;
    const labels = normalizedOptions
      .filter((o) => selected.includes(o.value))
      .map((o) => o.label);
    return labels.join(", ");
  }, [selected, normalizedOptions, placeholder]);

  // Compute maxHeight based on option row height (36px) + padding
  const maxHeight = `${36 * Math.max(1, Number(maxVisible || 3))}px`;

  return (
    <div className={`${styles.container} ${compact ? styles.compact : ""} ${className}`.trim()} ref={containerRef}>
      {label && (
        <label htmlFor={name} className={styles.label}>
          {label}
        </label>
      )}
      <button
        type="button"
        id={name}
        className={`${styles.control} ${disabled ? styles.disabled : ""}`.trim()}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={toggleOpen}
        disabled={disabled}
      >
        <span className={`${styles.valueText} ${!selected.length ? styles.placeholder : ""}`.trim()}>
          {selectedLabel}
        </span>
        <span className={styles.chevron} aria-hidden>
          ▾
        </span>
      </button>

      {open && (
        <div className={`${styles.dropdown} ${dropdownClassName}`.trim()} role="listbox" aria-multiselectable="true" style={{ maxHeight }}>
          {normalizedOptions.map((opt) => (
            <label key={opt.value} className={styles.option} title={opt.label}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={isChecked(opt.value)}
                onChange={() => handleToggle(opt.value)}
              />
              <span className={styles.optionLabel}>{opt.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

MultiSelectDropdown.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired, // string[] or {value, label}[]
  value: PropTypes.array, // selected values
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  maxVisible: PropTypes.number, // number of rows visible before scrolling
  disabled: PropTypes.bool,
  className: PropTypes.string,
  dropdownClassName: PropTypes.string,
};
